import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';

import { STORAGE_KEYS } from '@/platform/auth-runtime/token-storage';
import { useUserStore } from '@/platform/identity/store';
import AppLayout from '@/platform/layout/AppLayout.vue';

import authRoutes from './routes/auth';
import { isSafeNext } from './safe-next';
import { Path } from './targets';

// Auto-discover per-module v3 native route files.  Adding/removing
// `routes/<module>.ts` is enough — `index.ts` never changes.
//
// Auth routes are pulled in explicitly so they mount at the top level
// (outside AppLayout chrome) and resolve BEFORE the catch-all `:pathMatch`.
//
// Legacy is pulled out and appended LAST: it owns the `:pathMatch(.*)*`
// catch-all, which would otherwise shadow any native module whose glob
// sort key follows `legacy.ts` (e.g. `notifications.ts`) and route
// /notification → LegacyShellHost instead of NotificationListView.
// Vite's glob expansion is lexical, not insertion-order, so explicit
// reordering here is what guarantees the catch-all sits at the end.
const moduleEntries = Object.entries(
  import.meta.glob<{ default: RouteRecordRaw[] }>('./routes/*.ts', { eager: true }),
).filter(([path]) => !path.endsWith('/auth.ts'));
const nativeModuleRoutes = moduleEntries
  .filter(([path]) => !path.endsWith('/legacy.ts'))
  .flatMap(([, m]) => m.default);
const legacyModuleRoutes = moduleEntries
  .filter(([path]) => path.endsWith('/legacy.ts'))
  .flatMap(([, m]) => m.default);

export const routes: RouteRecordRaw[] = [
  ...authRoutes,
  {
    path: '/',
    component: AppLayout,
    children: [...nativeModuleRoutes, ...legacyModuleRoutes],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Unauthed traffic to any non-`authFree` path redirects to /login. Each route
// (or its parent) opts in via `meta.authFree`.
//
// Attach `next` only for the OAuth consent path (the sole destination the
// login/SSO round-trip is allowed to return to — see safe-next.ts). Every
// other unauthed hit lands on plain /login and, after sign-in, on the
// default destination.
router.beforeEach((to) => {
  if (to.meta?.authFree) return;
  const ubid = localStorage.getItem(STORAGE_KEYS.ubid);
  if (!ubid) {
    const query = isSafeNext(to.fullPath) ? { next: to.fullPath } : undefined;
    return { path: Path.Login, query, replace: true };
  }
});

// Per-navigation permission refresh. Legacy re-fetched /user-perms after every
// authed navigation, so a backend permission change surfaced on the next hop
// without waiting for the WS push; AppLayout's reactive gate then swaps the
// view. Gated like legacy — authFree routes and unauthenticated sessions skip
// it. Fire-and-forget: the axios interceptor owns any error toast.
//
// Only a real page-to-page navigation refreshes perms. List search / filter /
// pagination live in the URL query (useQueryStates), so they fire afterEach too
// — but re-fetching perms on every keystroke-search or page flip is wasted load,
// and legacy kept those filters in component state so they never counted as
// navigations. Skip same-path hops (query-only changes) to match that.
export function refreshPermsOnNav(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): void {
  if (to.meta?.authFree) return;
  if (!localStorage.getItem(STORAGE_KEYS.ubid)) return;
  if (to.path === from.path) return;
  void useUserStore()
    .fetchPerms()
    .catch(() => {});
}

router.afterEach(refreshPermsOnNav);
